import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import { link, routes } from '@aline/routes';
import { useMediaQuery, useTheme } from '@mui/material';
import { matchPath, parsePath, useLocation, useNavigate } from 'react-router-dom';
import { ExpandableDrawer } from 'src/components';
import { EditorRuntimeScope } from 'src/editor/editor-runtime-registry';
import { EditorSuggestionStateScope } from 'src/editor/editor-suggestion-state-registry';
import { EditorTabStateScope } from 'src/editor/editor-tab-state-registry';
import { makeStyles } from 'tss-react/mui';

import { AIChatDrawerContent } from './drawer-content/AIChatDrawerContent';
import { useIsActiveTabFocused } from './focused-view';
import { DrawerRouteParamsProvider, GlobalRightDrawerContent } from './GlobalRightDrawerContent';
import { TAB_PANEL_ID } from './tabs/tab-bar';
import { useTabContext } from './tabs/tab-context';
import { TabRouterProvider } from './tabs/tab-router-provider';
import type { Tab } from './tabs/tab-types';

const useStyles = makeStyles()(_theme => ({
  container: {
    display: 'flex',
    marginLeft: -1,
  },
  drawerContentRoot: {
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
    width: '100%',
  },
  drawerPane: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    overflowY: 'auto',
    width: '100%',
  },
}));

// ============================================================================
// Global Drawer Context - Provides drawer state management across the app
// ============================================================================

interface GlobalDrawerContextValue {
  closeDrawer: () => void;
  isOpen: boolean;
  openDrawer: () => void;
  toggleDrawer: () => void;
  variant: 'docked' | 'maximized';
}

const GlobalDrawerContext = createContext<GlobalDrawerContextValue | null>(null);

/**
 * Hook to access and control the global right drawer state.
 * Use this in any component that needs to open/close/toggle the drawer.
 */
export function useGlobalDrawer(): GlobalDrawerContextValue {
  const context = useContext(GlobalDrawerContext);
  if (!context) throw new Error('useGlobalDrawer must be used within GlobalDrawerProvider.');

  return context;
}

const DRAWER_CONFIG = {
  baseWidth: 340,
  breakpointsPx: {
    0: { drawerWidth: 300, screen: 1024 },
    1: { drawerWidth: 300, screen: 1280 },
    2: { drawerWidth: 360, screen: 1440 },
    3: { drawerWidth: 460, screen: 1680 },
    4: { drawerWidth: 560, screen: 1920 },
    5: { drawerWidth: 660, screen: 2560 },
  },
} as const;

function getIsBreakpointIndex(index: number): index is keyof typeof DRAWER_CONFIG.breakpointsPx {
  return index in DRAWER_CONFIG.breakpointsPx;
}

/**
 * Width of the full content row below the top bar (tab area + drawer). Used as
 * the drawer width in maximized mode so the singleton chat fills the page.
 */
function useContentRowWidth(): number | null {
  const [width, setWidth] = useState<number | null>(null);

  // Layout effect so the maximized width is known before the first paint.
  useLayoutEffect(() => {
    const element = document.getElementById(TAB_PANEL_ID);
    if (!element) return;

    setWidth(element.getBoundingClientRect().width);

    const resizeObserver = new ResizeObserver(() => {
      setWidth(element.getBoundingClientRect().width);
    });
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, []);

  return width;
}

function useDrawerWidthBasedOnBreakpoint(): number {
  const theme = useTheme();
  const breakpoints = [
    useMediaQuery(theme.breakpoints.up(DRAWER_CONFIG.breakpointsPx[0].screen), { noSsr: true }),
    useMediaQuery(theme.breakpoints.up(DRAWER_CONFIG.breakpointsPx[1].screen), { noSsr: true }),
    useMediaQuery(theme.breakpoints.up(DRAWER_CONFIG.breakpointsPx[2].screen), { noSsr: true }),
    useMediaQuery(theme.breakpoints.up(DRAWER_CONFIG.breakpointsPx[3].screen), { noSsr: true }),
    useMediaQuery(theme.breakpoints.up(DRAWER_CONFIG.breakpointsPx[4].screen), { noSsr: true }),
    useMediaQuery(theme.breakpoints.up(DRAWER_CONFIG.breakpointsPx[5].screen), { noSsr: true }),
  ];
  const lastMatchingIndex = breakpoints.lastIndexOf(true);
  if (!getIsBreakpointIndex(lastMatchingIndex)) return DRAWER_CONFIG.baseWidth;

  return DRAWER_CONFIG.breakpointsPx[lastMatchingIndex].drawerWidth;
}

export type DrawerRouteType =
  | 'ai'
  | 'comments'
  | 'fields'
  | 'add-input'
  | 'summary'
  | 'track-changes'
  | 'integrations'
  | 'playbooks'
  | 'document-preview'
  | 'reminder-details'
  | 'report-automations'
  | 'workflow-side-panel'
  | 'workflow-thread'
  | 'version-history'
  | 'default';

export type DrawerRouteMatch =
  | { params: { documentId: string }; type: 'ai' }
  | { params: { documentId: string }; type: 'comments' }
  | { params: { documentId: string }; type: 'fields' }
  | { params: { documentId: string }; type: 'add-input' }
  | { params: { documentId: string }; type: 'summary' }
  | { params: { documentId: string }; type: 'track-changes' }
  | { params: { documentId: string; integration?: string }; type: 'integrations' }
  | { params: { documentId: string }; type: 'playbooks' }
  | { params: { documentId: string }; type: 'document-preview' }
  | { params: { reminderId: string }; type: 'reminder-details' }
  | { params: { reportId: string }; type: 'report-automations' }
  | { params: { workflowId: string }; type: 'workflow-side-panel' }
  | { params: { workflowId: string }; type: 'workflow-thread' }
  | { params: { documentId: string; versionId: string }; type: 'version-history' }
  | { params: Record<string, never>; type: 'default' };

export function getDrawerRouteTypeFromPathname(pathname: string): DrawerRouteType {
  return getDrawerRouteMatchFromPathname(pathname).type;
}

export function getDrawerRouteMatchFromPathname(pathname: string): DrawerRouteMatch {
  const commentsMatch = matchPath(routes.DocumentDetailComments, pathname);
  if (commentsMatch?.params.documentId) {
    return { params: { documentId: commentsMatch.params.documentId }, type: 'comments' };
  }

  const fieldsMatch = matchPath(routes.DocumentDetailFields, pathname);
  if (fieldsMatch?.params.documentId) {
    return { params: { documentId: fieldsMatch.params.documentId }, type: 'fields' };
  }

  const addInputMatch = matchPath(routes.DocumentDetailAddInput, pathname);
  if (addInputMatch?.params.documentId) {
    return { params: { documentId: addInputMatch.params.documentId }, type: 'add-input' };
  }

  const summaryMatch = matchPath(routes.DocumentDetailSummary, pathname);
  if (summaryMatch?.params.documentId) {
    return { params: { documentId: summaryMatch.params.documentId }, type: 'summary' };
  }

  const trackedChangesMatch = matchPath(routes.DocumentDetailTrackedChanges, pathname);
  if (trackedChangesMatch?.params.documentId) {
    return {
      params: { documentId: trackedChangesMatch.params.documentId },
      type: 'track-changes',
    };
  }

  const integrationDetailMatch = matchPath(routes.DocumentDetailIntegrationDetail, pathname);
  if (integrationDetailMatch?.params.documentId && integrationDetailMatch.params.integration) {
    return {
      params: {
        documentId: integrationDetailMatch.params.documentId,
        integration: integrationDetailMatch.params.integration,
      },
      type: 'integrations',
    };
  }

  const integrationsMatch = matchPath(routes.DocumentDetailIntegrations, pathname);
  if (integrationsMatch?.params.documentId) {
    return { params: { documentId: integrationsMatch.params.documentId }, type: 'integrations' };
  }

  const playbooksMatch = matchPath(routes.DocumentDetailPlaybooks, pathname);
  if (playbooksMatch?.params.documentId) {
    return { params: { documentId: playbooksMatch.params.documentId }, type: 'playbooks' };
  }

  const aiMatch = matchPath(routes.DocumentDetailAi, pathname);
  if (aiMatch?.params.documentId) {
    return { params: { documentId: aiMatch.params.documentId }, type: 'ai' };
  }

  const repositoryPreviewMatch = matchPath(routes.RepositoryDocumentPreview, pathname);
  if (repositoryPreviewMatch?.params.documentId) {
    return {
      params: { documentId: repositoryPreviewMatch.params.documentId },
      type: 'document-preview',
    };
  }

  const partyPreviewMatch = matchPath(routes.PartyDetailDocumentPreview, pathname);
  if (partyPreviewMatch?.params.documentId) {
    return {
      params: { documentId: partyPreviewMatch.params.documentId },
      type: 'document-preview',
    };
  }

  const reminderMatch = matchPath(routes.RemindersPreview, pathname);
  if (reminderMatch?.params.reminderId) {
    return { params: { reminderId: reminderMatch.params.reminderId }, type: 'reminder-details' };
  }

  const reportMatch = matchPath(routes.ReportReportDetailAutomations, pathname);
  if (reportMatch?.params.reportId) {
    return { params: { reportId: reportMatch.params.reportId }, type: 'report-automations' };
  }

  /**
   * matchPath is exact (end: true by default in react-router v6), so the deeper
   * `…/task/:taskId/thread/:threadId` and `…/thread/:threadId` paths never match the shorter
   * workflow tab patterns — ordering relative to the task matcher is not load-bearing. Without
   * these explicit matchers the thread URLs fall through to 'default', which the drawer renders as
   * the global AI chat (an empty new-chat panel) instead of the AI thread.
   */
  const workflowTaskThreadMatch = matchPath(routes.WorkflowDetailTabTaskThread, pathname);
  if (workflowTaskThreadMatch?.params.workflowId) {
    return {
      params: { workflowId: workflowTaskThreadMatch.params.workflowId },
      type: 'workflow-thread',
    };
  }

  const workflowThreadMatch = matchPath(routes.WorkflowDetailTabThread, pathname);
  if (workflowThreadMatch?.params.workflowId) {
    return {
      params: { workflowId: workflowThreadMatch.params.workflowId },
      type: 'workflow-thread',
    };
  }

  const workflowTaskMatch = matchPath(routes.WorkflowDetailTabTask, pathname);
  if (workflowTaskMatch?.params.workflowId) {
    return {
      params: { workflowId: workflowTaskMatch.params.workflowId },
      type: 'workflow-side-panel',
    };
  }

  const workflowPhaseSettingsMatch = matchPath(routes.WorkflowDetailTabPhaseSettings, pathname);
  if (workflowPhaseSettingsMatch?.params.workflowId) {
    return {
      params: { workflowId: workflowPhaseSettingsMatch.params.workflowId },
      type: 'workflow-side-panel',
    };
  }

  const workflowDocumentsMatch = matchPath(routes.WorkflowDetailTabDocuments, pathname);
  if (workflowDocumentsMatch?.params.workflowId) {
    return {
      params: { workflowId: workflowDocumentsMatch.params.workflowId },
      type: 'workflow-side-panel',
    };
  }

  const workflowIntakeMatch = matchPath(routes.WorkflowDetailTabIntake, pathname);
  if (workflowIntakeMatch?.params.workflowId) {
    return {
      params: { workflowId: workflowIntakeMatch.params.workflowId },
      type: 'workflow-side-panel',
    };
  }

  const workflowCommentsMatch = matchPath(routes.WorkflowDetailTabComments, pathname);
  if (workflowCommentsMatch?.params.workflowId) {
    return {
      params: { workflowId: workflowCommentsMatch.params.workflowId },
      type: 'workflow-side-panel',
    };
  }

  const workflowCollaboratorsMatch = matchPath(routes.WorkflowDetailTabCollaborators, pathname);
  if (workflowCollaboratorsMatch?.params.workflowId) {
    return {
      params: { workflowId: workflowCollaboratorsMatch.params.workflowId },
      type: 'workflow-side-panel',
    };
  }

  const workflowOptionsMatch = matchPath(routes.WorkflowDetailTabOptions, pathname);
  if (workflowOptionsMatch?.params.workflowId) {
    return {
      params: { workflowId: workflowOptionsMatch.params.workflowId },
      type: 'workflow-side-panel',
    };
  }

  const templateDocumentsMatch = matchPath(routes.TemplateSelectedTemplateDocuments, pathname);
  if (templateDocumentsMatch?.params.workflowId) {
    return {
      params: { workflowId: templateDocumentsMatch.params.workflowId },
      type: 'workflow-side-panel',
    };
  }

  const templateCommentsMatch = matchPath(routes.TemplateSelectedTemplateComments, pathname);
  if (templateCommentsMatch?.params.workflowId) {
    return {
      params: { workflowId: templateCommentsMatch.params.workflowId },
      type: 'workflow-side-panel',
    };
  }

  const templateTeamMatch = matchPath(routes.TemplateSelectedTemplateTeam, pathname);
  if (templateTeamMatch?.params.workflowId) {
    return {
      params: { workflowId: templateTeamMatch.params.workflowId },
      type: 'workflow-side-panel',
    };
  }

  const templateOptionsMatch = matchPath(routes.TemplateSelectedTemplateOptions, pathname);
  if (templateOptionsMatch?.params.workflowId) {
    return {
      params: { workflowId: templateOptionsMatch.params.workflowId },
      type: 'workflow-side-panel',
    };
  }

  const templateStepsMatch = matchPath(routes.TemplateSelectedTemplateSteps, pathname);
  if (templateStepsMatch?.params.workflowId) {
    return {
      params: { workflowId: templateStepsMatch.params.workflowId },
      type: 'workflow-side-panel',
    };
  }

  const templateSummaryMatch = matchPath(routes.TemplateSelectedTemplateSummary, pathname);
  if (templateSummaryMatch?.params.workflowId) {
    return {
      params: { workflowId: templateSummaryMatch.params.workflowId },
      type: 'workflow-side-panel',
    };
  }

  const templateTasksMatch = matchPath(routes.TemplateSelectedTemplateTasks, pathname);
  if (templateTasksMatch?.params.workflowId) {
    return {
      params: { workflowId: templateTasksMatch.params.workflowId },
      type: 'workflow-side-panel',
    };
  }

  const versionHistoryMatch = matchPath(routes.DocumentDetailVersionDetail, pathname);
  if (versionHistoryMatch?.params.documentId && versionHistoryMatch.params.versionId) {
    return {
      params: {
        documentId: versionHistoryMatch.params.documentId,
        versionId: versionHistoryMatch.params.versionId,
      },
      type: 'version-history',
    };
  }

  return { params: {}, type: 'default' };
}

function getDrawerRouteTypeFromRoute(route: string) {
  return getDrawerRouteTypeFromPathname(parsePath(route).pathname ?? '/');
}

function getIsGlobalDrawerRouteType(type: DrawerRouteType) {
  return type === 'default' || type === 'ai' || type === 'workflow-thread';
}

// Accepts a full route (with search/hash) or a plain pathname.
function getIsChatRoute(route: string) {
  const pathname = parsePath(route).pathname ?? '/';
  return Boolean(matchPath({ end: false, path: routes.ChatIndex }, pathname));
}

function getEditorDocumentIdFromRoute(route: string) {
  const pathname = parsePath(route).pathname ?? '/';
  const match = matchPath({ end: false, path: routes.DocumentDetail }, pathname);
  return match?.params.documentId ?? null;
}

/**
 * Determines the current drawer route type based on the URL.
 */
export function useDrawerRouteType(): DrawerRouteType {
  const location = useLocation();
  return getDrawerRouteTypeFromPathname(location.pathname);
}

/**
 * Checks if the current route is a specific drawer content route.
 */
function useIsSpecificDrawerRoute(): boolean {
  const location = useLocation();
  return getDrawerRouteTypeFromPathname(location.pathname) !== 'default';
}

/**
 * Custom hook that manages the drawer state.
 * The drawer is toggleable by the user in docked mode. Chat tabs maximize the
 * singleton chat panel instead of rendering a separate full-page ChatPanel.
 */
function useDrawerState(): GlobalDrawerContextValue {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeTab } = useTabContext();
  const isFocused = useIsActiveTabFocused();
  const isSpecificRoute = useIsSpecificDrawerRoute();
  const isEditorPage = Boolean(
    matchPath({ end: false, path: routes.DocumentDetail }, location.pathname)
  );
  const [toggleOpen, setToggleOpen] = useState(() => isSpecificRoute || isEditorPage);
  const activeTabIsChat = activeTab ? getIsChatRoute(activeTab.route) : false;
  const variant = activeTabIsChat || getIsChatRoute(location.pathname) ? 'maximized' : 'docked';
  /**
   * The open state is shared by every tab. A focused tab whose route would show the
   * global AI chat has nothing to put in the drawer, so it reads as closed there
   * without forgetting that the document tab left it open.
   */
  const hasNoFocusedContent =
    isFocused && getIsGlobalDrawerRouteType(getDrawerRouteTypeFromRoute(activeTab?.route ?? '/'));
  const isOpen = variant === 'maximized' || (toggleOpen && !hasNoFocusedContent);

  // Auto-open drawer when navigating to a specific drawer route (e.g. deep link, preview click).
  useEffect(() => {
    if (isSpecificRoute) setToggleOpen(true);
  }, [isSpecificRoute]);

  // Auto-open drawer when navigating to an editor page.
  useEffect(() => {
    if (isEditorPage) setToggleOpen(true);
  }, [isEditorPage]);

  const openDrawer = useCallback(() => setToggleOpen(true), []);

  const closeDrawer = useCallback(() => {
    if (variant === 'maximized') return;

    if (isSpecificRoute) {
      const pathname = location.pathname;
      const nav = (route: string) => navigate(route, { replace: true });

      // Editor: just close the drawer, keep the URL so content doesn't swap mid-animation.
      if (matchPath({ end: false, path: routes.DocumentDetail }, pathname)) {
        setToggleOpen(false);
        return;
      }

      const repoMatch = matchPath(routes.RepositoryDocumentPreview, pathname);
      if (repoMatch) {
        nav(link.RepositoryDocuments());
        setToggleOpen(false);
        return;
      }

      const partyMatch = matchPath(routes.PartyDetailDocumentPreview, pathname);
      if (partyMatch?.params.partyId) {
        nav(link.PartyDetailDocuments({ partyId: partyMatch.params.partyId }));
        setToggleOpen(false);
        return;
      }

      const reminderMatch = matchPath(routes.RemindersPreview, pathname);
      if (reminderMatch) {
        nav(link.RemindersIndex());
        setToggleOpen(false);
        return;
      }

      const reportMatch = matchPath(routes.ReportReportDetailAutomations, pathname);
      if (reportMatch?.params.reportId) {
        nav(link.ReportDetail({ reportId: reportMatch.params.reportId }));
        setToggleOpen(false);
        return;
      }

      const workflowMatch = matchPath(routes.WorkflowDetail, pathname);
      if (workflowMatch?.params.workflowId) {
        nav(link.WorkflowDetail({ workflowId: workflowMatch.params.workflowId }));
        setToggleOpen(false);
        return;
      }

      const templateMatch = matchPath(
        { end: false, path: routes.TemplateSelectedTemplateIndex },
        pathname
      );
      if (templateMatch?.params.workflowId) {
        nav(link.TemplateSelectedTemplateIndex({ workflowId: templateMatch.params.workflowId }));
        setToggleOpen(false);
        return;
      }
    }
    setToggleOpen(false);
  }, [isSpecificRoute, location.pathname, navigate, variant]);

  const toggleDrawer = useCallback(() => {
    if (variant === 'maximized') return;
    setToggleOpen(prev => !prev);
  }, [variant]);

  return useMemo(
    () => ({ closeDrawer, isOpen, openDrawer, toggleDrawer, variant }),
    [closeDrawer, isOpen, openDrawer, toggleDrawer, variant]
  );
}

/**
 * Provider component that wraps the app and provides global drawer state.
 * Must be placed above any components that use useGlobalDrawer().
 */
export function GlobalDrawerProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const drawerState = useDrawerState();

  return (
    <GlobalDrawerContext.Provider value={drawerState}>{children}</GlobalDrawerContext.Provider>
  );
}

export function GlobalRightDrawer(): JSX.Element {
  const { classes } = useStyles();
  const { isOpen, variant } = useGlobalDrawer();
  const isMaximized = variant === 'maximized';
  const { activeTab, tabs } = useTabContext();
  const contentRowWidth = useContentRowWidth();
  const drawerWidthBasedOnBreakpoint = useDrawerWidthBasedOnBreakpoint();
  const drawerWidth =
    isMaximized && contentRowWidth ? contentRowWidth : drawerWidthBasedOnBreakpoint;
  const activeDrawerRouteType = activeTab
    ? getDrawerRouteTypeFromRoute(activeTab.route)
    : 'default';
  const showGlobalAi = isMaximized || getIsGlobalDrawerRouteType(activeDrawerRouteType);

  return (
    <div className={classes.container}>
      <ExpandableDrawer
        draggable
        dragBoundsPx={{
          left: drawerWidthBasedOnBreakpoint * 0.5,
          right: drawerWidthBasedOnBreakpoint * 0.25,
        }}
        maximized={isMaximized}
        open={isOpen}
        position="right"
        width={drawerWidth}
      >
        <div className={classes.drawerContentRoot}>
          <React.Activity mode={showGlobalAi ? 'visible' : 'hidden'}>
            <div className={classes.drawerPane}>
              <GlobalAiDrawerPane activeTab={activeTab} />
            </div>
          </React.Activity>
          {tabs.map(tab => (
            <TabDrawerPane
              key={tab.id}
              isActive={tab.id === activeTab?.id}
              showGlobalAi={showGlobalAi}
              tab={tab}
            />
          ))}
        </div>
      </ExpandableDrawer>
    </div>
  );
}

/**
 * The chat drawer needs the active tab's editor scopes, not just the runtime
 * scope: suggestion-card navigation reads `useEditorSuggestionState()` (which
 * silently falls back to an empty no-op state without an
 * `EditorSuggestionStateScope` ancestor, making card clicks do nothing) and
 * the scroll math reads `useEditorTabStateSelector`. These wrappers are plain
 * context providers — they re-render on state changes but never remount the
 * drawer content, so they don't reintroduce the tab-switch reloads that
 * ENG-5303 fixed.
 */
function GlobalAiDrawerPane({ activeTab }: { activeTab: Tab | undefined }): JSX.Element {
  if (!activeTab) return <></>;

  return (
    <TabRouterProvider isActive tab={activeTab}>
      <EditorRuntimeScope
        documentId={getEditorDocumentIdFromRoute(activeTab.route)}
        tabId={activeTab.id}
      >
        <EditorTabStateScope tabId={activeTab.id}>
          <EditorSuggestionStateScope tabId={activeTab.id}>
            <DrawerRouteParamsProvider>
              <AIChatDrawerContent />
            </DrawerRouteParamsProvider>
          </EditorSuggestionStateScope>
        </EditorTabStateScope>
      </EditorRuntimeScope>
    </TabRouterProvider>
  );
}

function TabDrawerPane({
  isActive,
  showGlobalAi,
  tab,
}: {
  isActive: boolean;
  showGlobalAi: boolean;
  tab: Tab;
}): JSX.Element | null {
  const { classes } = useStyles();
  const documentId = getEditorDocumentIdFromRoute(tab.route);
  const drawerRouteType = getDrawerRouteTypeFromRoute(tab.route);
  const shouldRenderPane = !getIsGlobalDrawerRouteType(drawerRouteType);
  const shouldShowPane = isActive && !showGlobalAi;

  if (!shouldRenderPane) return null;

  return (
    <React.Activity mode={shouldShowPane ? 'visible' : 'hidden'}>
      <TabRouterProvider isActive={isActive} tab={tab}>
        <EditorRuntimeScope documentId={documentId} tabId={tab.id}>
          <EditorTabStateScope tabId={tab.id}>
            <EditorSuggestionStateScope tabId={tab.id}>
              <div className={classes.drawerPane}>
                <GlobalRightDrawerContent />
              </div>
            </EditorSuggestionStateScope>
          </EditorTabStateScope>
        </EditorRuntimeScope>
      </TabRouterProvider>
    </React.Activity>
  );
}
